/**
 * Contrato de leitura do motor de estados (épicos motor-estados / mapa-ocupacao-vagas).
 *
 * Fonte: GET {culture}/api/StationState (envelope { success, data }) e o hub SignalR
 * /hubs/station-state (método StateChanged, mesmo payload). Feature somente leitura (AC-5).
 *
 * Os campos chegam em camelCase tanto no REST quanto no push; ainda assim
 * `normalizeStationState` aceita PascalCase como defesa contra variação de serialização.
 */

/** Estado da estação (StationStateEnum) — não persistido, derivado na leitura. */
export type StationStateKey =
  | "Produzindo"
  | "CicloExcedido"
  | "Ausencia"
  | "FaltaMaterial";

/** Tipo da vaga no layout (VagaTypeEnum). `null` para vaga fora do layout. */
export type VagaTypeKey = "Operacao" | "Supermercado" | "Extra";

/** Estado de ocupação da vaga (VagaOccupancyStateEnum). */
export type VagaOccupancyStateKey = "Livre" | "Ocupada";

export interface OpenCycle {
  vagaKey: string;
  startedAt: string | null;
  elapsedSeconds: number | null;
}

export interface Vaga {
  vagaKey: string;
  /** Operacao | Supermercado | Extra | null (fora do layout). AC-3. */
  type: VagaTypeKey | null;
  /** Livre | Ocupada. AC-1. */
  state: VagaOccupancyStateKey | string;
  /** Instante da última mudança (relógio da borda). `null` se ainda sem evento. */
  since: string | null;
  /** Segundos no estado atual (relógio do servidor). `null` se ainda sem evento. AC-2. */
  elapsedSeconds: number | null;
}

export interface StationState {
  stationId: string;
  state: StationStateKey | string;
  stateSince: string | null;
  openCycles: OpenCycle[];
  vagas: Vaga[];
}

const numOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Lê a primeira chave presente (tolera camelCase e PascalCase). */
const pick = (obj: any, ...keys: string[]): any => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

/**
 * Normaliza o payload do REST/hub para o formato interno, tolerante a casing.
 * Retorna `null` se o objeto não for aproveitável.
 */
export const normalizeStationState = (raw: any): StationState | null => {
  if (!raw || typeof raw !== "object") return null;

  const vagasRaw = pick(raw, "vagas", "Vagas");
  const cyclesRaw = pick(raw, "openCycles", "OpenCycles");

  return {
    stationId: pick(raw, "stationId", "StationId") ?? "",
    state: pick(raw, "state", "State") ?? "",
    stateSince: pick(raw, "stateSince", "StateSince") ?? null,
    openCycles: (Array.isArray(cyclesRaw) ? cyclesRaw : []).map((c: any) => ({
      vagaKey: pick(c, "vagaKey", "VagaKey") ?? "",
      startedAt: pick(c, "startedAt", "StartedAt") ?? null,
      elapsedSeconds: numOrNull(pick(c, "elapsedSeconds", "ElapsedSeconds")),
    })),
    vagas: (Array.isArray(vagasRaw) ? vagasRaw : []).map((v: any) => ({
      vagaKey: pick(v, "vagaKey", "VagaKey") ?? "",
      type: (pick(v, "type", "Type") ?? null) as VagaTypeKey | null,
      state: pick(v, "state", "State") ?? "",
      since: pick(v, "since", "Since") ?? null,
      elapsedSeconds: numOrNull(pick(v, "elapsedSeconds", "ElapsedSeconds")),
    })),
  };
};
